import { useState } from 'react';
import { Button, Modal, Form, Select, Input, message, Alert, Space, Typography } from 'antd';
import { AlertOutlined, PhoneOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  supabase,
  SOS_EVENT_TYPES,
  submitSOSEvent,
  getCurrentOrgId,
  formatTime,
  type Trip,
} from '@ubus/shared';
import { translateClientTripStatus, translateSosEventType } from '../utils/clientStatusLabels';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

interface ClientSOSButtonProps {
  tripId?: string;
  compact?: boolean;
}

type SosTripOption = Pick<
  Trip,
  | 'id'
  | 'date'
  | 'pickup_time'
  | 'pickup_address'
  | 'dropoff_address'
  | 'status'
  | 'source'
  | 'assignment_status'
  | 'vehicle_id'
  | 'driver_id'
>;

function shortPlace(address?: string | null, fallback = '—'): string {
  if (!address?.trim()) return fallback;
  return address.split(',')[0]?.trim() || address.trim();
}

function sortSosTrips(trips: SosTripOption[], today: string): SosTripOption[] {
  const rank = (t: SosTripOption) => {
    if (t.status === 'in_progress') return 0;
    if (t.date === today && t.status === 'dispatched') return 1;
    if (t.date === today) return 2;
    return 3;
  };
  return [...trips].sort((a, b) => {
    const dr = rank(a) - rank(b);
    if (dr !== 0) return dr;
    const dd = a.date.localeCompare(b.date);
    if (dd !== 0) return dd;
    return (a.pickup_time || '').localeCompare(b.pickup_time || '');
  });
}

function ClientSOSButton({ tripId, compact = false }: ClientSOSButtonProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [trips, setTrips] = useState<SosTripOption[]>([]);
  const [form] = Form.useForm();
  const [userPhone, setUserPhone] = useState<string>('');

  const tripOptionLabel = (trip: SosTripOption): string => {
    const when = `${dayjs(trip.date).format('DD MMM')} ${formatTime(trip.pickup_time)}`;
    const route = `${shortPlace(trip.pickup_address, t('common.noData'))} → ${shortPlace(trip.dropoff_address, t('common.noData'))}`;
    const status = translateClientTripStatus(t, trip);
    return `${when} · ${route} · ${status}`;
  };

  const handleOpen = async () => {
    setVisible(true);
    setLoadingTrips(true);
    form.resetFields();

    const today = dayjs().format('YYYY-MM-DD');
    const end = dayjs().add(7, 'day').format('YYYY-MM-DD');
    const start = dayjs().subtract(1, 'day').format('YYYY-MM-DD');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: org } = await supabase
          .from('organizations')
          .select('contact_phone')
          .ilike('contact_email', user.email ?? '')
          .maybeSingle();

        const phone = org?.contact_phone || user.user_metadata?.phone || '';
        setUserPhone(phone);
        form.setFieldsValue({ contact_phone: phone, event_type: 'Vehicle Not Appearing' });
      }

      const orgId = await getCurrentOrgId();
      if (!orgId) {
        setTrips([]);
        return;
      }

      const { data, error } = await supabase
        .from('trips')
        .select('id, date, pickup_time, pickup_address, dropoff_address, status, source, assignment_status, vehicle_id, driver_id')
        .eq('org_id', orgId)
        .in('status', ['scheduled', 'dispatched', 'in_progress'])
        .gte('date', start)
        .lte('date', end)
        .order('date', { ascending: true })
        .order('pickup_time', { ascending: true });

      if (error) throw error;

      const sorted = sortSosTrips((data ?? []) as SosTripOption[], today);
      setTrips(sorted);

      const preselect =
        (tripId && sorted.find((trip) => trip.id === tripId)?.id) ||
        sorted[0]?.id ||
        undefined;
      if (preselect) {
        form.setFieldsValue({ trip_id: preselect });
      }
    } catch (err) {
      console.error('Error loading SOS context:', err);
      message.error(t('sos.loadFailed'));
      setTrips([]);
    } finally {
      setLoadingTrips(false);
    }
  };

  const handleSubmit = async (values: {
    trip_id: string;
    event_type: string;
    description?: string;
    contact_phone: string;
  }) => {
    if (!values.trip_id) {
      message.error(t('sos.selectTripError'));
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await submitSOSEvent({
        tripId: values.trip_id,
        reporterRole: 'client',
        eventType: values.event_type,
        description: values.description || null,
        contactPhone: values.contact_phone || null,
      });

      if (error) {
        message.error(t('sos.submitFailed', { message: error.message }));
      } else {
        message.success(t('sos.submitSuccess'));
        form.resetFields();
        setVisible(false);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unexpected error';
      message.error(t('sos.unexpectedError', { message: msg }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setVisible(false);
  };

  const noTrips = !loadingTrips && trips.length === 0;

  return (
    <>
      <Button
        danger
        type={compact ? 'default' : 'primary'}
        icon={<AlertOutlined />}
        onClick={() => void handleOpen()}
        size="middle"
        style={{ fontWeight: 600 }}
      >
        {compact ? t('sos.compact') : t('sos.button')}
      </Button>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ExclamationCircleFilled style={{ color: '#ff4d4f', fontSize: 22 }} />
            <span>{t('sos.modalTitle')}</span>
          </div>
        }
        open={visible}
        onCancel={handleCancel}
        footer={null}
        width={520}
        destroyOnHidden
      >
        <Alert
          type="error"
          message={t('sos.warningTitle')}
          description={t('sos.warningDesc')}
          showIcon
          style={{ marginBottom: 20 }}
        />

        {noTrips && (
          <Alert
            type="warning"
            showIcon
            message={t('sos.noTripsTitle')}
            description={t('sos.noTripsDesc')}
            style={{ marginBottom: 20 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => void handleSubmit(v)}
          initialValues={{
            event_type: 'Vehicle Not Appearing',
            contact_phone: userPhone,
          }}
        >
          <Form.Item
            name="trip_id"
            label={t('sos.tripLabel')}
            rules={[{ required: true, message: t('sos.selectTrip') }]}
          >
            <Select
              placeholder={loadingTrips ? t('sos.loadingTrips') : t('sos.selectTripPlaceholder')}
              size="large"
              loading={loadingTrips}
              disabled={noTrips}
              showSearch
              optionFilterProp="label"
              options={trips.map((trip) => ({
                value: trip.id,
                label: tripOptionLabel(trip),
              }))}
            />
          </Form.Item>

          <Form.Item
            name="event_type"
            label={t('sos.issueType')}
            rules={[{ required: true, message: t('sos.selectIssueType') }]}
          >
            <Select placeholder={t('sos.issueTypePlaceholder')} size="large" disabled={noTrips}>
              {SOS_EVENT_TYPES.map((type) => (
                <Option key={type} value={type}>
                  {translateSosEventType(t, type)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="description" label={t('sos.description')}>
            <TextArea
              rows={4}
              placeholder={t('sos.descriptionPlaceholder')}
              maxLength={500}
              showCount
              disabled={noTrips}
            />
          </Form.Item>

          <Form.Item
            name="contact_phone"
            label={t('sos.contactPhone')}
            rules={[{ required: true, message: t('sos.contactPhoneRequired') }]}
          >
            <Input
              prefix={<PhoneOutlined />}
              placeholder={t('sos.contactPhonePlaceholder')}
              size="large"
              disabled={noTrips}
            />
          </Form.Item>

          {noTrips && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>
              {t('sos.submitUnavailable')}
            </Text>
          )}

          <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handleCancel} size="large">
                {t('common.cancel')}
              </Button>
              <Button
                type="primary"
                danger
                htmlType="submit"
                loading={submitting}
                disabled={noTrips || loadingTrips}
                size="large"
                icon={<AlertOutlined />}
              >
                {t('sos.submitReport')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default ClientSOSButton;
